import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../config';
import Avatar from './Avatar';
import RatingRing from './RatingRing';
import ClockButton from './ClockButton';
import { callNumber } from './rcWidget';
import './PowerDialer.css';

// Power dialer (October 2026). One person on the line, one call at a time: the recruiter presses
// Call for the first candidate, picks what happened (required), and Next dials the following one.
// "No answer" puts the candidate back at the end of this session's queue once (and keeps them on
// the Today list as a retry, routes/candidate-activity.js). Opened from the Today bar on Driver
// Candidates with either the Today call list or the current table rows as the queue.
export const OUTCOMES = ['Answered', 'No answer', 'Left voicemail', 'Wrong number', 'Not interested'];
const MAX_TRIES_PER_SESSION = 2;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function PowerDialer({ queue: initialQueue, sourceLabel, tags = {}, renderProgress, onTimeline, onClose }) {
  const [queue, setQueue] = useState(initialQueue);
  const [pos, setPos] = useState(0);
  const [called, setCalled] = useState(false);       // current candidate has been dialed
  const [outcome, setOutcome] = useState(null);
  const [tries, setTries] = useState({});            // candidate id -> dials this session
  const [stats, setStats] = useState({ called: 0, answered: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const current = queue[pos] || null;
  const finished = pos >= queue.length;

  const dial = useCallback(c => {
    if (!c) return;
    callNumber(c.phone);
    axios.post(`${API_URL}/api/candidates/${c.id}/call-started`, {}, { headers: headers() }).catch(() => {});
    setTries(t => ({ ...t, [c.id]: (t[c.id] || 0) + 1 }));
    setStats(s => ({ ...s, called: s.called + 1 }));
    setCalled(true);
    setOutcome(null);
  }, []);

  // Save the outcome, then move on. dialNext = Next (dials straight away); false = Pause.
  const advance = useCallback(async (dialNext) => {
    if (!current || !outcome || saving) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/api/candidates/${current.id}/call-outcome`, { outcome }, { headers: headers() });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the outcome. Try again.');
      setSaving(false);
      return;
    }
    if (outcome === 'Answered') setStats(s => ({ ...s, answered: s.answered + 1 }));
    let q = queue;
    if (outcome === 'No answer' && (tries[current.id] || 0) < MAX_TRIES_PER_SESSION) {
      q = [...queue, { ...current, __retry: true }];
      setQueue(q);
    }
    const next = pos + 1;
    setPos(next);
    setCalled(false);
    setOutcome(null);
    setSaving(false);
    if (dialNext && q[next]) dial(q[next]);
  }, [current, outcome, saving, queue, tries, pos, dial]);

  const skip = () => { setPos(p => p + 1); setCalled(false); setOutcome(null); setError(''); };

  // Keyboard: 1-5 pick the outcome, Enter = Next (not while typing somewhere)
  useEffect(() => {
    const onKey = e => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
      if (called && /^[1-5]$/.test(e.key)) { setOutcome(OUTCOMES[Number(e.key) - 1]); e.preventDefault(); }
      else if (e.key === 'Enter' && called && outcome) { advance(true); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [called, outcome, advance]);

  const close = () => onClose({ called: stats.called, answered: stats.answered });

  return (
    <div className="pd" role="region" aria-label="Power dialer">
      <div className="pd-head">
        <span className="pd-title">Power dialer</span>
        <span className="pd-src">{sourceLabel}</span>
        <span className="pd-count">{finished ? `${queue.length} of ${queue.length}` : `${pos + 1} of ${queue.length}`}</span>
        <span className="pd-stats"><b>{stats.called}</b> called · <b>{stats.answered}</b> answered</span>
        <button type="button" className="pd-end" onClick={close}>End session</button>
      </div>

      {finished ? (
        <div className="pd-done">
          <b>List finished.</b> {stats.called} calls, {stats.answered} answered.
          <button type="button" className="pd-btn primary" onClick={close}>Close</button>
        </div>
      ) : current && (
        <div className="pd-body">
          <div className="pd-who">
            <Avatar name={current.candidate_name} size="sm" />
            <div className="pd-name-block">
              <div className="pd-name">
                {current.candidate_name}
                <ClockButton onClick={() => onTimeline(current.id)} />
                {current.__retry && <span className="pd-retry">Second try</span>}
              </div>
              <div className="pd-sub">
                <span className="pd-phone">{String(current.phone || '').replace(/\D/g, '')}</span>
                {current.csa_name && <span>{current.csa_name}</span>}
                {current.company_name && <span>{current.company_name}</span>}
                {tags[current.id] && <span className="pd-tag">{tags[current.id].label}</span>}
              </div>
            </div>
            <RatingRing rating={current.mountain_rating || null} />
            <div className="pd-progress">{renderProgress(current)}</div>
          </div>
          {current.remarks && <div className="pd-remarks"><span>Remarks:</span> {current.remarks}</div>}

          <div className="pd-actions">
            {!called ? (
              <>
                <button type="button" className="pd-btn call" onClick={() => dial(current)}>📞 Call {current.candidate_name.split(' ')[0]}</button>
                <button type="button" className="pd-btn" onClick={skip}>Skip</button>
              </>
            ) : (
              <>
                <div className="pd-outcomes" role="radiogroup" aria-label="What happened?">
                  {OUTCOMES.map((o, i) => (
                    <button key={o} type="button" role="radio" aria-checked={outcome === o}
                      className={`pd-outcome${outcome === o ? ' on' : ''}${o === 'Answered' ? ' good' : ''}`}
                      onClick={() => setOutcome(o)}>
                      <kbd>{i + 1}</kbd>{o}
                    </button>
                  ))}
                </div>
                <div className="pd-next">
                  <button type="button" className="pd-btn" onClick={() => dial(current)} title="Dial this candidate again">Redial</button>
                  <button type="button" className="pd-btn" disabled={!outcome || saving} onClick={() => advance(false)}>Save & pause</button>
                  <button type="button" className="pd-btn primary" disabled={!outcome || saving}
                    onClick={() => advance(true)} title={outcome ? 'Save and call the next candidate (Enter)' : 'Pick what happened first'}>
                    {queue[pos + 1] ? `Next: call ${queue[pos + 1].candidate_name.split(' ')[0]} →` : 'Finish'}
                  </button>
                </div>
              </>
            )}
          </div>
          {!outcome && called && <div className="pd-hint">Pick what happened (1-5), then Next or Enter.</div>}
          {error && <div className="pd-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
